import { getID } from 'utils/id';
import { collectFingerprint } from './collect';
import { pick } from 'utils/object';

type Fingerprint = Awaited<ReturnType<typeof collectFingerprint>>;

/**
 * Prefix 1 — device class. Invariant under Brave Standard farbling AND user
 * behavior. hardwareConcurrency and deviceMemory are intentionally excluded:
 * Brave rotates its farbling key per session, so those values drift between
 * visits for the same device.
 */
function extractStableSignals(fp: Fingerprint) {
  return {
    ...pick(fp.screen, ['width', 'height', 'colorDepth']),
    ...pick(fp.hardware, ['maxTouchPoints', 'platform']),
    ...pick(fp.browser, ['pdfViewerEnabled', 'cookieEnabled']),
    ...pick(fp.media, ['videoCodecs', 'audioCodecs']),
    ...pick(fp.storage, ['localStorageAvailable', 'sessionStorageAvailable', 'indexedDBAvailable']),
    ...pick(fp.math, ['tan', 'atan2', 'acos', 'sinh']),
  };
}

/**
 * Prefix 2 — user/profile class. Stable per browser profile but can shift on
 * user action (browser zoom, language change, travel). Disambiguates two
 * devices that share the same prefix 1.
 */
function extractSecondarySignals(fp: Fingerprint) {
  return {
    ...pick(fp.screen, ['pixelRatio', 'availWidth', 'availHeight', 'orientation']),
    ...pick(fp.browser, ['language', 'languages']),
    ...pick(fp.timezone, ['timezone', 'timezoneOffset']),
  };
}

function deterministicSerialize(signals: Record<string, unknown>) {
  return JSON.stringify(signals, (_key, value) => {
    if (value === undefined || (typeof value === 'number' && isNaN(value))) {
      return null;
    }

    return value;
  });
}

/**
 * SHA-256 → first `bits` bits → base36. Base36 is alphanumeric, so `_` stays
 * usable as a separator and the output is URL/log-safe.
 *
 * 120 bits → 24 chars. Birthday-bound collision risk is ~50% at 2^60 devices,
 * negligible for identification at realistic scales.
 *
 * function is only correct when bits % 8 === 0
 */
async function shortHash(input: string, bits = 120): Promise<string> {
  if (bits <= 0 || bits > 256 || bits % 8 !== 0) {
    throw new Error(`bits must be a byte-aligned integer between 8 and 256`);
  }

  const byteLength = Math.ceil(bits / 8);
  const encoded = new TextEncoder().encode(input);
  const buffer = await crypto.subtle.digest('SHA-256', encoded);
  const view = new Uint8Array(buffer, 0, byteLength);

  let value = 0n;

  for (const byte of view) {
    value = (value << 8n) | BigInt(byte);
  }

  const maxChars = Math.ceil((bits * Math.LN2) / Math.log(36));

  return value.toString(36).padStart(maxChars, '0');
}

/**
 * Format: `{stable}_{secondary}_{fpjsVisitorId}`.
 *
 * - stable groups the same physical device across Brave farbling sessions
 * - secondary disambiguates users who share identical device class
 * - fpjsVisitorId carries canvas/audio/fonts/WebGL entropy (may drift in Brave)
 *
 * If FPJS is blocked, the suffix falls back to a client-generated id.
 */
export async function getFingerprint() {
  const fingerprint = await collectFingerprint();

  const [stableHash, secondaryHash] = await Promise.all([
    shortHash(deterministicSerialize(extractStableSignals(fingerprint))),
    shortHash(deterministicSerialize(extractSecondarySignals(fingerprint))),
  ]);

  const suffix = fingerprint.deviceId || getID();

  return {
    version: '1',

    id: `${stableHash}_${secondaryHash}_${suffix}`,

    deviceId: fingerprint.deviceId,

    qualifier: stableHash,

    secondaryQualifier: secondaryHash,

    raw: fingerprint,
  };
}
